import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, Wallet, AlertCircle, HandCoins, Banknote, Lock, FileClock } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { tk, bn, todayStr, fmtTime, fmtDate } from "../lib/format";
import { Loader, GlassPage, GlassHeader } from "../components/kit";

const ACCENT: Record<string, { glow: string; text: string; ring: string }> = {
  blue: { glow: "bg-blue-500/20 text-blue-300", text: "text-white", ring: "from-blue-400/30 to-blue-600/10" },
  green: { glow: "bg-emerald-500/20 text-emerald-300", text: "text-emerald-300", ring: "from-emerald-400/30 to-emerald-600/10" },
  red: { glow: "bg-rose-500/20 text-rose-300", text: "text-rose-300", ring: "from-rose-400/30 to-rose-600/10" },
};

function GStat({ label, value, icon: Icon, tone, testid, hint }: any) {
  const a = ACCENT[tone];
  return (
    <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-4 md:p-5 fade-up" data-testid={testid}>
      <div className={`absolute -top-10 -right-10 w-24 h-24 rounded-full bg-gradient-to-br ${a.ring} blur-2xl pointer-events-none`} />
      <div className="relative flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-300">{label}</p>
        <span className={`w-9 h-9 rounded-xl grid place-items-center ${a.glow}`}><Icon size={18} /></span>
      </div>
      <p className={`num mt-2 text-xl md:text-2xl font-bold ${a.text}`} data-testid={`${testid}-value`}>{value}</p>
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function Panel({ title, children }: any) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl overflow-hidden">
      <h2 className="text-[16px] font-semibold text-white px-5 py-4 border-b border-white/10">{title}</h2>
      {children}
    </div>
  );
}
const EmptyDark = () => <div className="py-10 text-center text-slate-500 text-sm">কোনো তথ্য নেই</div>;

export default function Daily() {
  const [date, setDate] = useState(todayStr());
  const [d, setD] = useState<any>(null);
  const [closing, setClosing] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    setD(null);
    api.get("/dashboard", { params: { date } }).then((r) => setD(r.data));
  }, [date]);

  const closeDay = async () => {
    if (!window.confirm(`${fmtDate(date)} তারিখের দিন ক্লোজ করে রিপোর্ট তৈরি করবেন?`)) return;
    setClosing(true);
    try {
      const r = await api.post("/day-close", { date });
      toast.success("দিন ক্লোজ হয়েছে — রিপোর্ট তৈরি");
      nav(`/reports/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setClosing(false);
    }
  };

  return (
    <GlassPage testid="daily-report-page">
      <GlassHeader
        title="দৈনিক হিসাব"
        subtitle={`তারিখ: ${fmtDate(date)}`}
        actions={<div className="flex flex-wrap gap-2">
          <input type="date" className="ginp w-44" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-date-input" />
          <button onClick={() => nav("/reports")} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white/5 border border-white/10 text-slate-200 hover:bg-white/10 font-semibold transition-all active:scale-95" data-testid="daily-reports-link"><FileClock size={17} /> রিপোর্ট হিস্ট্রি</button>
          <button onClick={closeDay} disabled={closing} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-semibold transition-all active:scale-95 shadow-lg shadow-emerald-500/25" data-testid="day-close-button"><Lock size={17} /> {closing ? "ক্লোজ হচ্ছে..." : "দিন ক্লোজ"}</button>
        </div>}
      />
      {!d ? <Loader /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <GStat label="মোট বিক্রি" value={tk(d.total_sale)} icon={TrendingUp} tone="blue" testid="daily-total-sale" hint={`${bn(d.memo_count)} টি মেমো`} />
            <GStat label="বিক্রিতে পরিশোধ" value={tk(d.total_paid)} icon={Wallet} tone="green" testid="daily-total-paid" />
            <GStat label="নতুন বাকি" value={tk(d.total_due)} icon={AlertCircle} tone="red" testid="daily-total-due" />
            <GStat label="বাকি আদায়" value={tk(d.total_collection)} icon={HandCoins} tone="green" testid="daily-total-collection" />
            <GStat label="মোট ক্যাশ এসেছে" value={tk(d.cash_in)} icon={Banknote} tone="green" testid="daily-cash-in" />
          </div>
          <div className="grid lg:grid-cols-2 gap-4 mt-5">
            <Panel title="এই দিনের মেমো">
              {d.day_memos.length === 0 ? <EmptyDark /> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="daily-memos-table">
                    <thead><tr className="text-slate-400 text-left"><th className="px-5 py-2.5 font-medium">মেমো</th><th className="px-3 py-2.5 font-medium">ক্রেতা</th><th className="px-3 py-2.5 font-medium text-right">মোট</th><th className="px-5 py-2.5 font-medium text-right">পরিশোধ</th></tr></thead>
                    <tbody>{d.day_memos.map((m: any) => (
                      <tr key={m.id} className="border-t border-white/5 cursor-pointer hover:bg-white/5" onClick={() => nav(`/memos/${m.id}`)}>
                        <td className="px-5 py-3"><b className="text-blue-300">{m.invoice_no}</b><span className="block text-xs text-slate-500">{fmtTime(m.created_at)}</span></td>
                        <td className="px-3 py-3 text-slate-200">{m.customer?.name}</td>
                        <td className="px-3 py-3 text-right num font-semibold text-white">{tk(m.grand_total)}</td>
                        <td className="px-5 py-3 text-right num text-emerald-300">{tk(m.paid)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </Panel>
            <Panel title="এই দিনের আদায়">
              {d.day_payments.length === 0 ? <EmptyDark /> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="daily-payments-table">
                    <thead><tr className="text-slate-400 text-left"><th className="px-5 py-2.5 font-medium">রশিদ</th><th className="px-3 py-2.5 font-medium">ক্রেতা</th><th className="px-3 py-2.5 font-medium">মাধ্যম</th><th className="px-5 py-2.5 font-medium text-right">আদায়</th></tr></thead>
                    <tbody>{d.day_payments.map((p: any) => (
                      <tr key={p.id} className="border-t border-white/5 hover:bg-white/5"><td className="px-5 py-3 font-semibold text-white">{p.receipt_no}</td><td className="px-3 py-3 text-slate-200">{p.customer_name}</td><td className="px-3 py-3 text-slate-300">{p.method}</td><td className="px-5 py-3 text-right num font-bold text-emerald-300">{tk(p.amount)}</td></tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>
        </>
      )}
    </GlassPage>
  );
}
